import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaEye, FaArrowRight, FaChevronLeft, FaChevronRight,
  FaQuoteLeft, FaCircleCheck
} from "react-icons/fa6";
import { HiOutlineLightBulb } from "react-icons/hi2";

// Assets
import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import atric from "../assets/atric-2.jpg";

const sliderImages = [atric, banner1, banner2, banner3, banner4];

const coreValues = [
  { letter: "A", title: "Accountability", desc: "Owning outcomes with full responsibility." },
  { letter: "T", title: "Timeliness", desc: "Delivery on Time" },
  { letter: "R", title: "Responsiveness", desc: "Prompt, value-driven client engagement." },
  { letter: "I", title: "Integrity", desc: "Ethical conduct in every professional action." },
  { letter: "C", title: "Competency", desc: "High standards of skill and expert knowledge." }
];

export default function AboutUsContent() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % sliderImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const slideVariants = {
    initial: { opacity: 0, scale: 1.1, x: 50 },
    animate: { opacity: 1, scale: 1, x: 0 },
    exit: { opacity: 0, scale: 0.95, x: -50 }
  };

  return (
    <section className="about-modern-refined py-5">
      <div className="container py-lg-5">
        
        {/* --- SECTION 1: ASYMMETRIC INTRO --- */}
        <div className="row g-5 align-items-stretch mb-5 pb-lg-5">
          <div className="col-lg-5 d-flex flex-column justify-content-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="atric-h2 mb-4">
                ATRIC <span>CONSULTANTS</span>
              </h2>
              <p className="atric-p mb-4">
                Your trusted partner in Audit & Assurance, Accounting, Taxation and Advisory across diverse industries with over 15 years of expertise. We offer tailored solutions and uncompromising professionalism.
              </p>
              
              <div className="atric-features-list mb-4">
                {["NBAA Certified", "10+ Years Experience", "NGO & Corporate Specialists"].map((item, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-2">
                    <FaCircleCheck className="text-primary" />
                    <span className="fw-semibold text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="col-lg-7">
            <div className="atric-slider-wrapper">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  variants={slideVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.8, ease: "circOut" }}
                  className="atric-slider-item"
                  style={{ backgroundImage: `url(${sliderImages[current]})` }}
                />
              </AnimatePresence>
              
              {/* Glass Navigation Overlay */}
              <div className="atric-slider-nav">
                <div className="d-flex align-items-center gap-3">
                  <button onClick={() => setCurrent(current === 0 ? sliderImages.length - 1 : current - 1)} className="nav-btn">
                    <FaChevronLeft />
                  </button>
                  <div className="nav-counter">
                    <span className="text-white">0{current + 1}</span>
                    <span className="text-white-50 mx-2">/</span>
                    <span className="text-white-50">0{sliderImages.length}</span>
                  </div>
                  <button onClick={() => setCurrent((current + 1) % sliderImages.length)} className="nav-btn">
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- SECTION 2: VISION / MISSION SPLIT --- */}
        <div className="row g-4 mb-5 pb-5">
          <div className="col-md-6">
            <div className="atric-glass-card h-100">
              <div className="card-accent-line" />
              <div className="p-5">
                <FaEye className="text-primary mb-4" size={40} />
                <h3 className="fw-bold text-slate-900">Our Vision</h3>
                <p className="text-slate-600 mb-0">To be the leading Consulting Firm in East Africa.</p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="atric-glass-card h-100 dark">
              <div className="p-5">
                <HiOutlineLightBulb className="text-info mb-4" size={45} />
                <h3 className="fw-bold text-white">Our Mission</h3>
                <p className="text-white-50 mb-0">To deliver high-quality, timely professional services that help our clients effectively manage and scale their businesses and projects.</p>
              </div>
            </div>
          </div>
        </div>

        {/* --- SECTION 3: THE ATRIC VALUES (MODERN GRID) --- */}
        <div className="text-center mb-5">
          <h2 className="atric-h2">The <span className="text-primary">ATRIC</span> Pillars</h2>
        </div>

        <div className="row g-3">
          {coreValues.map((v, i) => (
            <div key={i} className="col-lg-2 col-md-4 col-6 flex-grow-1">
              <motion.div 
                whileHover={{ y: -8 }}
                className="atric-value-square"
              >
                <div className="square-letter">{v.letter}</div>
                <div className="square-content">
                  <h6 className="font-bold text-dark mb-1">{v.title}</h6>
                  <p className="x-small text-muted mb-0">{v.desc}</p>
                </div>
              </motion.div>
            </div>
          ))}
        </div>

      </div>

      <style>{`
        .about-modern-refined {
          background-color: #ffffff;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }

        .atric-eyebrow {
          color: #0284c7;
          font-weight: 800;
          font-size: 0.8rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .atric-h2 {
          font-weight: 900;
          font-size: clamp(2.2rem, 5vw, 3.2rem);
          color: #0f172a;
          line-height: 1.1;
          letter-spacing: -1px;
        }

        .atric-p {
          color: #475569;
          font-size: 1.15rem;
          line-height: 1.8;
        }

        /* --- THE SLIDER --- */
        .atric-slider-wrapper {
          position: relative;
          height: 500px;
          border-radius: 40px;
          overflow: hidden;
          background: #f1f5f9;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
        }

        .atric-slider-item {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
        }

        .atric-slider-nav {
          position: absolute;
          bottom: 30px;
          right: 30px;
          background: rgba(15, 23, 42, 0.75);
          backdrop-filter: blur(12px);
          padding: 12px 24px;
          border-radius: 100px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          z-index: 10;
        }

        .nav-btn {
          background: none;
          border: none;
          color: white;
          font-size: 1rem;
          transition: 0.3s;
          padding: 5px;
        }

        .nav-btn:hover { color: #38bdf8; transform: scale(1.1); }

        .nav-counter { font-family: 'JetBrains Mono', monospace; font-size: 0.9rem; }

        /* --- CARDS --- */
        .atric-glass-card {
          background: #fff;
          border-radius: 30px;
          border: 1px solid #f1f5f9;
          position: relative;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
          transition: 0.3s;
        }

        .atric-glass-card.dark { background: #0f172a; }

        .card-accent-line {
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: #0284c7;
        }

        /* --- VALUE SQUARES --- */
        .atric-value-square {
          background: #f8fafc;
          border-radius: 24px;
          padding: 30px 20px;
          height: 100%;
          text-align: center;
          position: relative;
          overflow: hidden;
          border: 1px solid #f1f5f9;
        }

        .square-letter {
          position: absolute;
          top: -10px;
          right: -5px;
          font-size: 5rem;
          font-weight: 900;
          color: rgba(2, 132, 199, 0.04);
          line-height: 1;
        }

        .square-content { position: relative; z-index: 2; }
        .x-small { font-size: 0.75rem; }

        @media (max-width: 991px) {
          .atric-slider-wrapper { height: 350px; border-radius: 24px; }
          .atric-slider-nav { bottom: 15px; right: 15px; padding: 8px 16px; }
        }
      `}</style>
    </section>
  );
}